import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

export const AUTH_TOKEN_KEY = 'authToken';

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URL, // now points to your server IP
});

// A JWT from an earlier guest-checkout/login lingers in localStorage long
// after it expires. The backend now tolerates an expired/invalid token on
// any request (falls back to anonymous instead of failing the whole query —
// see apps.users.backends.JSONWebTokenBackend), but there's no reason for
// this public site to keep re-sending a token it can tell is already dead
// on every single request. Decoded by hand (base64url JSON, no signature
// check needed client-side) rather than pulling in a dependency for one
// field read.
function isTokenExpired(token) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === 'number' && exp * 1000 <= Date.now();
  } catch {
    return true; // unparseable — can't be a usable token either
  }
}

// Nothing was ever attaching the JWT issued at login/guest-checkout to
// outgoing requests — every request went out unauthenticated regardless of
// whether a token existed, so any resolver that checks
// info.context.user.is_authenticated (e.g. agencyPaymentMethods) always
// failed with "Authentication required." Reading it fresh from storage on
// every request (rather than once at client-creation time) means a token
// saved after this client already exists — e.g. right after BookTour
// returns one — is picked up on the very next call.
const authLink = setContext((_, { headers }) => {
  let token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token && isTokenExpired(token)) {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    token = null;
  }
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(`[GraphQL error]: Message: ${message}, Path: ${path}`);
    });
  }
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          tours: { merge: false },
          experiences: { merge: false },
          countries: { merge: false },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: { fetchPolicy: 'cache-and-network' },
    query: { fetchPolicy: 'network-only' },
  },
});

export default client;
